import React, { useState, useEffect, useRef } from 'react';
import { 
  Send, Search, MoreVertical, Phone, Video, 
  Info, ArrowLeft, Plus, Users, Lock, Check, CheckCheck,
  Paperclip, X, Image as ImageIcon, File, Download,
  Smile, Edit2, Trash2, Reply, Shield, Circle, UserPlus,
  Star, Archive, BellOff, Forward, Pin, Bell, Mic, Pause, Play
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { playNotificationSound } from '../../utils/notificationSound';

const ModernMessaging = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Get user avatar with priority: cdn_avatar_url > avatar > Employee.cdn_avatar_url > Employee.avatar
  const getUserAvatar = () => {
    return user?.cdn_avatar_url || 
           user?.avatar || 
           user?.Employee?.cdn_avatar_url || 
           user?.Employee?.avatar || 
           null;
  };
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [typing, setTyping] = useState(false);
  const [recipientTyping, setRecipientTyping] = useState(false);
  const [activeTab, setActiveTab] = useState('open'); // 'open' or 'archived'
  const [viewingImage, setViewingImage] = useState(null);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  
  // New features state
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(null);
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [pinnedChats, setPinnedChats] = useState(() => {
    const saved = localStorage.getItem('pinnedChats');
    return saved ? JSON.parse(saved) : [];
  });
  const [archivedChats, setArchivedChats] = useState(() => {
    const saved = localStorage.getItem('archivedChats');
    return saved ? JSON.parse(saved) : [];
  });
  const [mutedChats, setMutedChats] = useState(() => {
    const saved = localStorage.getItem('mutedChats');
    return saved ? JSON.parse(saved) : [];
  });
  const [starredMessages, setStarredMessages] = useState(() => {
    const saved = localStorage.getItem('starredMessages');
    return saved ? JSON.parse(saved) : [];
  });
  const [messageReactions, setMessageReactions] = useState(() => {
    const saved = localStorage.getItem('messageReactions');
    return saved ? JSON.parse(saved) : {};
  });
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  const notificationPermissionRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const audioPlayerRef = useRef({});
  const selectedConversationRef = useRef(selectedConversation);
  selectedConversationRef.current = selectedConversation;
  
  const currentEmployeeId = user?.Employee?.id || user?.employee?.id || user?.id;
  const userAvatar = getUserAvatar();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkToEmployee = searchParams.get('to');

  // Read the notification permission state (never requests it here - the
  // opt-in banner in the layout handles permission UX).
  useEffect(() => {
    if ('Notification' in window) {
      notificationPermissionRef.current = Notification.permission === 'granted';
    }
  }, []);

  // Global realtime listener now owns the alert sound/popup behavior.
  const showNotification = () => {};

  // Check for new messages and notify
  useEffect(() => {
    if (messages.length > lastMessageCountRef.current && lastMessageCountRef.current > 0) {
      const newMessages = messages.slice(lastMessageCountRef.current);
      
      newMessages.forEach(msg => {
        // Only notify for messages from others
        if (msg.from_employee_id !== currentEmployeeId) {
          showNotification(
            msg.from_employee_name || 'Someone',
            msg.message || 'Sent a file',
            msg.from_employee_avatar
          );
        }
      });
    }
    
    lastMessageCountRef.current = messages.length;
  }, [messages, currentEmployeeId]);

  // Load conversations and employees on mount
  useEffect(() => {
    loadConversations();
    loadEmployees();
  }, []);

  // Safety-net poll for the sidebar conversation list, so a new conversation
  // (or an updated last-message preview) always shows up on this page without
  // a manual refresh, even if the socket event is missed.
  useEffect(() => {
    const interval = setInterval(() => {
      loadConversations();
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Socket listener for real-time direct messages
  const { socket, isConnected, setViewingConversation, clearViewingConversation } = useWebSocket();

  // ── In-app toast for incoming messages (WhatsApp-style) ────────────────────
  // The voice alert ("Excuse me boss...") is owned by the global
  // RealTimeNotificationListener so it fires the same way on every page,
  // including this one - it is NOT triggered here to avoid double-speaking.
  const notifyIncomingMessage = (data) => {
    const isIncoming = data.from_employee_id !== currentEmployeeId;
    if (!isIncoming) return;

    try {
      toast(`${data.from_employee_name || 'Someone'} sent you a message`, {
        icon: '💬',
        duration: 4000,
      });
    } catch (error) {
      // Toast must never break message handling.
    }
  };

  // ── Tell the service worker whether we are viewing this conversation, so it
  // can suppress duplicate native push notifications. ────────────────────────
  useEffect(() => {
    const handleSwCheck = (event) => {
      if (!event.data || event.data.type !== 'PUSH_CONVERSATION_CHECK') return;
      const conv = selectedConversationRef.current;
      const viewing = !!conv && event.data.fromEmployeeId !== null && conv.employee_id === Number(event.data.fromEmployeeId);
      if (event.source) {
        event.source.postMessage({
          type: 'PUSH_CONVERSATION_RESULT',
          viewingConversation: viewing,
        });
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSwCheck);
    }
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSwCheck);
      }
    };
  }, [currentEmployeeId]);

  // ── Signal the backend which conversation is open (viewing) ───────────────
  useEffect(() => {
    if (!socket || !isConnected) return;
    if (selectedConversation) {
      setViewingConversation(selectedConversation.employee_id);
    } else {
      clearViewingConversation();
    }
    return () => clearViewingConversation();
  }, [socket, isConnected, selectedConversation, setViewingConversation, clearViewingConversation]);

  // ── Deep link support: /collaboration/messaging?to=<employeeId> ───────────
  // Opens the conversation with the given employee once conversations load.
  useEffect(() => {
    if (!deepLinkToEmployee || !conversations.length) return;
    const target = conversations.find(
      (c) => c.employee_id === Number(deepLinkToEmployee),
    );
    if (target) {
      setSelectedConversation(target);
      setSearchParams({}, { replace: true });
    }
  }, [deepLinkToEmployee, conversations, setSearchParams]);

  // Register socket listener when connection becomes available
  useEffect(() => {
    console.log('🔌 [ModernMessaging] direct_message:new listener effect ran:', { hasSocket: !!socket, isConnected });
    if (!socket || !isConnected) return;
    console.log('✅ [ModernMessaging] direct_message:new listener REGISTERED on socket', socket.id);

    const handleNewMessage = (data) => {
      const conv = selectedConversationRef.current;
      console.log('📩 [ModernMessaging] handleNewMessage fired:', { data, conv, currentEmployeeId });

      // WhatsApp-style: play the chime + toast for incoming messages that are
      // not visible in the currently open conversation.
      notifyIncomingMessage(data);

      // ── 1. Update the open conversation's message list instantly ──────────
      if (conv) {
        // The partner in the open conversation is conv.employee_id.
        // A new message belongs to this conversation when one of the
        // two participants is the partner (regardless of who sent it).
        const involvesPartner =
          data.from_employee_id === conv.employee_id ||
          data.to_employee_id === conv.employee_id;
        console.log('🔍 [ModernMessaging] involvesPartner:', involvesPartner, { convEmployeeId: conv.employee_id, dataFrom: data.from_employee_id, dataTo: data.to_employee_id });

        if (involvesPartner) {
          setMessages(prev => {
            console.log('✏️ [ModernMessaging] setMessages updater running, prev.length:', prev.length);
            // Replace any optimistic (temp-*) message that matches this real one
            const hasOptimistic = prev.some(
              m =>
                typeof m.id === 'string' &&
                m.id.startsWith('temp-') &&
                m.from_employee_id === data.from_employee_id &&
                m.to_employee_id === data.to_employee_id
            );
            if (hasOptimistic) {
              // Swap the newest matching temp message with the real one
              let replaced = false;
              return prev.map(m => {
                if (
                  !replaced &&
                  typeof m.id === 'string' &&
                  m.id.startsWith('temp-') &&
                  m.from_employee_id === data.from_employee_id &&
                  m.to_employee_id === data.to_employee_id
                ) {
                  replaced = true;
                  return data;
                }
                return m;
              });
            }
            // Skip real duplicates (same numeric id)
            if (prev.some(m => m.id === data.id)) return prev;
            return [...prev, data];
          });

          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      }

      // ── 2. Always keep the conversation sidebar in sync ───────────────────
      setConversations(prev => {
        const partnerId =
          data.from_employee_id === currentEmployeeId
            ? data.to_employee_id
            : data.from_employee_id;
        const partnerName =
          data.from_employee_id === currentEmployeeId
            ? data.to_employee_name
            : data.from_employee_name;
        const partnerAvatar =
          data.from_employee_id === currentEmployeeId
            ? data.to_employee_avatar
            : data.from_employee_avatar;
        const isIncoming = data.from_employee_id !== currentEmployeeId;

        const existing = prev.find(c => c.employee_id === partnerId);
        if (existing) {
          return prev
            .map(c =>
              c.employee_id === partnerId
                ? {
                    ...c,
                    last_message: data.message,
                    last_message_time: data.created_at,
                    unread_count: isIncoming ? c.unread_count + 1 : c.unread_count,
                  }
                : c
            )
            // Bubble the updated conversation to top
            .sort((a, b) =>
              new Date(b.last_message_time) - new Date(a.last_message_time)
            );
        }

        // Brand-new conversation
        return [
          {
            employee_id: partnerId,
            employee_name: partnerName,
            avatar_url: partnerAvatar,
            last_message: data.message,
            last_message_time: data.created_at,
            unread_count: isIncoming ? 1 : 0,
          },
          ...prev,
        ];
      });
    };

    socket.on('direct_message:new', handleNewMessage);
    return () => socket.off('direct_message:new', handleNewMessage);
  }, [socket, isConnected, currentEmployeeId]);

  // ── Safety-net poll for the open conversation ──────────────────────────────
  // Guarantees a new message always shows up without leaving/refreshing the
  // page, even in the rare case the socket event is missed. Quiet refetch
  // only: no loading spinner, no forced scroll reset, no mark-as-read - it
  // just merges in whatever the server has that isn't already in state.
  useEffect(() => {
    const employeeId = selectedConversation?.employee_id;
    if (!employeeId) return;

    const poll = async () => {
      try {
        const response = await api.get(`/direct-messaging/messages/${employeeId}`);
        const fresh = response.data.data?.messages || [];
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newOnes = fresh.filter(m => !existingIds.has(m.id));
          if (newOnes.length === 0) return prev;

          newOnes.forEach(m => {
            if (m.from_employee_id !== currentEmployeeId) {
              playNotificationSound();
            }
          });

          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);

          return [...prev, ...newOnes].sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );
        });
      } catch (error) {
        // Safety net must never break the page.
      }
    };

    const interval = setInterval(poll, 4000);
    return () => clearInterval(interval);
  }, [selectedConversation?.employee_id, currentEmployeeId]);

  // Load messages when conversation selected
  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.employee_id);
    }
  }, [selectedConversation]);

  // Scroll on new messages - Force scroll to bottom
  useEffect(() => {
    if (messages.length > 0) {
      // Immediate scroll
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      // Delayed scroll to ensure content is rendered
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
      // Additional scroll after slight delay for images/content
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, [messages]);
  
  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem('pinnedChats', JSON.stringify(pinnedChats));
  }, [pinnedChats]);
  
  useEffect(() => {
    localStorage.setItem('archivedChats', JSON.stringify(archivedChats));
  }, [archivedChats]);
  
  useEffect(() => {
    localStorage.setItem('mutedChats', JSON.stringify(mutedChats));
  }, [mutedChats]);
  
  useEffect(() => {
    localStorage.setItem('starredMessages', JSON.stringify(starredMessages));
  }, [starredMessages]);
  
  useEffect(() => {
    localStorage.setItem('messageReactions', JSON.stringify(messageReactions));
  }, [messageReactions]);

  // Auto scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Pin/Unpin chat
  const togglePinChat = (employeeId) => {
    setPinnedChats(prev => {
      if (prev.includes(employeeId)) {
        toast.success('Chat unpinned');
        return prev.filter(id => id !== employeeId);
      } else {
        toast.success('Chat pinned to top');
        return [...prev, employeeId];
      }
    });
  };
  
  // Archive/Unarchive chat
  const toggleArchiveChat = (employeeId) => {
    setArchivedChats(prev => {
      if (prev.includes(employeeId)) {
        toast.success('Chat unarchived');
        return prev.filter(id => id !== employeeId);
      } else {
        toast.success('Chat archived');
        setSelectedConversation(null);
        return [...prev, employeeId];
      }
    });
  };
  
  // Mute/Unmute chat
  const toggleMuteChat = (employeeId) => {
    setMutedChats(prev => {
      if (prev.includes(employeeId)) {
        toast.success('Chat unmuted');
        return prev.filter(id => id !== employeeId);
      } else {
        toast.success('Chat muted');
        return [...prev, employeeId];
      }
    });
  };
  
  // Star/Unstar message
  const toggleStarMessage = (messageId) => {
    setStarredMessages(prev => {
      if (prev.includes(messageId)) {
        toast.success('Message unstarred');
        return prev.filter(id => id !== messageId);
      } else {
        toast.success('Message starred');
        return [...prev, messageId];
      }
    });
  };
  
  // Add reaction to message
  const addReaction = (messageId, emoji) => {
    setMessageReactions(prev => {
      const messageReacts = prev[messageId] || [];
      const userReaction = messageReacts.find(r => r.userId === currentEmployeeId);
      
      let updated;
      if (userReaction) {
        // Remove if same emoji, replace if different
        if (userReaction.emoji === emoji) {
          updated = messageReacts.filter(r => r.userId !== currentEmployeeId);
        } else {
          updated = messageReacts.map(r => 
            r.userId === currentEmployeeId ? { ...r, emoji } : r
          );
        }
      } else {
        // Add new reaction
        updated = [...messageReacts, { userId: currentEmployeeId, emoji }];
      }
      
      return { ...prev, [messageId]: updated };
    });
    setShowReactionPicker(null);
  };
  
  // Start editing message
  const startEditMessage = (msg) => {
    setEditingMessage(msg);
    setMessageInput(msg.message);
  };
  
  // Cancel editing
  const cancelEdit = () => {
    setEditingMessage(null);
    setMessageInput('');
  };
  
  // Forward message
  const forwardMessage = async (message, recipientId) => {
    try {
      await api.post('/direct-messaging/messages', {
        to_employee_id: recipientId,
        message: `📨 Forwarded: ${message.message}`,
        file_url: message.file_url,
        file_type: message.file_type
      });
      
      toast.success('Message forwarded');
      setShowForwardModal(null);
    } catch (error) {
      console.error('Forward error:', error);
      toast.error('Failed to forward message');
    }
  };

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        
        // Create preview URL
        const previewUrl = URL.createObjectURL(audioBlob);
        setAudioPreviewUrl(previewUrl);
        
        console.log('Recording stopped:', {
          size: audioBlob.size,
          type: audioBlob.type,
          duration: recordingTime
        });
        
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Update timer every second
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Recording started');
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setAudioPreviewUrl(null);
    setRecordingTime(0);
    toast.info('Recording cancelled');
  };

  const sendVoiceMessage = async () => {
    if (!audioBlob || !selectedConversation) return;

    try {
      const formData = new FormData();
      
      // Try sending as audio/mpeg (MP3) - more compatible with CDN
      const mp3Blob = new Blob([audioBlob], { type: 'audio/mpeg' });
      formData.append('file', mp3Blob, `voice-${Date.now()}.mp3`);
      formData.append('to_employee_id', selectedConversation.employee_id);
      formData.append('message', `🎤 Voice message (${formatDuration(recordingTime)})`);

      console.log('Sending voice message:', {
        blobSize: mp3Blob.size,
        blobType: mp3Blob.type,
        recipientId: selectedConversation.employee_id,
        duration: recordingTime
      });

      const response = await api.post('/direct-messaging/messages/with-file', formData, {
        timeout: 30000
      });

      console.log('Voice message sent:', response.data);

      setAudioBlob(null);
      setRecordingTime(0);
      await loadMessages(selectedConversation.employee_id);
      await loadConversations();
      toast.success('Voice message sent');
    } catch (error) {
      console.error('Send voice error:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMsg = error.response?.data?.error || 
                       error.response?.data?.message || 
                       error.message || 
                       'Failed to send voice message';
      toast.error(errorMsg);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleAudioPlayback = (audioUrl, messageId) => {
    const audio = audioPlayerRef.current[messageId];
    
    if (audio && !audio.paused) {
      audio.pause();
      setPlayingAudio(null);
    } else {
      // Pause all other audio
      Object.values(audioPlayerRef.current).forEach(a => a.pause());
      setPlayingAudio(null);

      // Play this audio
      if (!audio) {
        const newAudio = new Audio(audioUrl);
        audioPlayerRef.current[messageId] = newAudio;
        newAudio.onended = () => setPlayingAudio(null);
        newAudio.play();
        setPlayingAudio(messageId);
      } else {
        audio.play();
        setPlayingAudio(messageId);
      }
    }
  };

  const loadConversations = async () => {
    try {
      console.log('📥 Loading conversations for user:', {
        userId: user?.id,
        employeeId: currentEmployeeId,
        email: user?.email
      });
      
      const response = await api.get('/direct-messaging/conversations');
      
      const convList = response.data.data?.conversations || [];
      const apiEmployeeId = response.data.data?.currentEmployeeId;
      
      console.log('✅ Loaded conversations:', {
        count: convList.length,
        currentEmployeeId: apiEmployeeId,
        conversations: convList.map(c => `${c.employee_name} (ID: ${c.employee_id})`)
      });
      
      setConversations(convList);
    } catch (error) {
      console.error('❌ Error loading conversations:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      }
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await api.get('/collaboration/staff', {
        params: { limit: 1000 }
      });
      
      const allEmployees = response.data.data?.staff || [];
      const filtered = allEmployees.filter(emp => emp.id !== currentEmployeeId);
      setEmployees(filtered);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadMessages = async (employeeId) => {
    try {
      setLoading(true);
      console.log('📥 Loading messages with:', {
        currentUser: user?.email,
        currentEmployeeId,
        otherEmployeeId: employeeId
      });
      
      const response = await api.get(`/direct-messaging/messages/${employeeId}`);
      
      const messagesList = response.data.data?.messages || [];
      const apiEmployeeId = response.data.data?.currentEmployeeId;
      
      console.log('✅ Loaded messages:', {
        count: messagesList.length,
        currentEmployeeId: apiEmployeeId,
        conversation: `${apiEmployeeId} <-> ${employeeId}`,
        firstMessage: messagesList[0] ? {
          from: messagesList[0].from_employee_id,
          to: messagesList[0].to_employee_id,
          preview: messagesList[0].message?.substring(0, 30)
        } : 'No messages'
      });
      
      setMessages(messagesList);
      
      // Mark as read
      await api.put(`/direct-messaging/messages/${employeeId}/mark-read`);
      
      // FORCE SCROLL TO BOTTOM - Multiple attempts
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 0);
      
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
      
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    } catch (error) {
      console.error('❌ Error loading messages:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        toast.error('Authentication required');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() && !selectedFile) return;
    if (!selectedConversation) return;

    const messageContent = messageInput.trim();
    setMessageInput('');
    setTyping(false);

    try {
      // Handle editing existing message
      if (editingMessage) {
        // Optimistic update for edit
        setMessages(prev => prev.map(msg => 
          msg.id === editingMessage.id 
            ? { ...msg, message: messageContent, is_edited: true, edited_at: new Date() }
            : msg
        ));
        
        setEditingMessage(null);
        
        await api.put(`/direct-messaging/messages/${editingMessage.id}`, {
          message: messageContent
        });
        
        toast.success('Message edited');
        return;
      }

      // Handle new message with optional reply
      if (selectedFile) {
        // Create optimistic message for file
        const tempId = `temp-${Date.now()}`;
        const optimisticMessage = {
          id: tempId,
          message_id: tempId,
          from_employee_id: currentEmployeeId,
          from_employee_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'You',
          to_employee_id: selectedConversation.employee_id,
          to_employee_name: selectedConversation.employee_name || 'Unknown User',
          message: messageContent || `📎 ${selectedFile.name}`,
          file_url: URL.createObjectURL(selectedFile),
          file_type: selectedFile.type.startsWith('image/') ? 'image' : 'file',
          is_read: false,
          is_deleted: false,
          created_at: new Date().toISOString(),
          sending: true // Mark as sending
        };
        
        // Add to messages immediately
        setMessages(prev => [...prev, optimisticMessage]);
        setTimeout(scrollToBottom, 100);
        
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('to_employee_id', selectedConversation.employee_id);
        formData.append('message', messageContent || `📎 ${selectedFile.name}`);
        if (replyingTo) {
          formData.append('reply_to_message_id', replyingTo.id);
        }

        const response = await api.post('/direct-messaging/messages/with-file', formData);
        
        // The socket event will deliver the real message — just clear the temp one
        const realMsg = response.data?.data?.message;
        if (realMsg) {
          setMessages(prev => prev.map(msg => msg.id === tempId ? realMsg : msg));
        } else {
          setMessages(prev => prev.filter(msg => msg.id !== tempId));
        }

        setSelectedFile(null);
        toast.success('File sent successfully');
      } else {
        // Create optimistic message for instant local feedback
        const tempId = `temp-${Date.now()}`;
        const optimisticMessage = {
          id: tempId,
          message_id: tempId,
          from_employee_id: currentEmployeeId,
          from_employee_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'You',
          to_employee_id: selectedConversation.employee_id,
          to_employee_name: selectedConversation.employee_name || 'Unknown User',
          message: messageContent,
          file_url: null,
          file_type: 'text',
          is_read: false,
          is_deleted: false,
          reply_to_message_id: replyingTo?.id || null,
          created_at: new Date().toISOString(),
          sending: true,
        };

        setMessages(prev => [...prev, optimisticMessage]);
        setTimeout(scrollToBottom, 100);

        const payload = { to_employee_id: selectedConversation.employee_id, message: messageContent };
        if (replyingTo) payload.reply_to_message_id = replyingTo.id;

        const response = await api.post('/direct-messaging/messages', payload);

        // Replace temp with real — the socket event also fires but the
        // dedup logic in handleNewMessage will recognise the temp swap.
        const realMsg = response.data?.data?.message;
        if (realMsg) {
          setMessages(prev => prev.map(msg => msg.id === tempId ? realMsg : msg));
        }
      }

      setReplyingTo(null);
      // Sidebar is kept in sync by the socket handler — no loadConversations() needed
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      setMessageInput(messageContent);
      
      // Remove failed message
      setMessages(prev => prev.filter(msg => !msg.sending));
    }
  };

  const startNewChat = (employee) => {
    const employeeName = employee.User 
      ? `${employee.User.first_name || ''} ${employee.User.last_name || ''}`.trim()
      : `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
    
    // Priority: cdn_avatar_url > avatar > User.cdn_avatar_url > User.avatar
    const avatarUrl = employee.cdn_avatar_url || 
                      employee.avatar || 
                      employee.User?.cdn_avatar_url || 
                      employee.User?.avatar;
    
    // Get position
    const position = employee.position || 'Employee';

    setSelectedConversation({
      employee_id: employee.id,
      employee_name: employeeName || 'Unknown User',
      position: position,  // Added position
      avatar_url: avatarUrl,
      cdn_avatar_url: avatarUrl
    });
    
    setShowNewChatModal(false);
    setSearchQuery('');
    loadMessages(employee.id);
  };

  const handleTyping = (e) => {
    setMessageInput(e.target.value);
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set typing indicator
    if (e.target.value.trim() && !typing) {
      setTyping(true);
    }
    
    // Clear typing after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      setTyping(false);
    }, 2000);
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    
    try {
      await api.delete(`/direct-messaging/messages/${messageId}`);
      
      // Update local state immediately to show "Message deleted"
      setMessages(prev => 
        prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, is_deleted: true, message: 'This message was deleted' } 
            : msg
        )
      );
      
      toast.success('Message deleted');
    } catch (error) {
      console.error('Error deleting message:', error);
      const errorMsg = error.response?.data?.message || 'Failed to delete message';
      toast.error(errorMsg);
    }
  };

  const formatTime = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Max 10MB
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    // Allowed file types
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain', 'text/csv'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('File type not supported. Please use images, PDFs, or documents.');
      return;
    }
    
    setSelectedFile(file);
    toast.success(`File selected: ${file.name}`);
  };

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || url.split('/').pop() || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const getFilePreview = (file) => {
    if (!file) return null;
    
    const isImage = file.type.startsWith('image/');
    if (isImage) {
      return (
        <div className="relative">
          <img 
            src={URL.createObjectURL(file)} 
            alt={file.name}
            className="w-full h-32 object-cover rounded-lg"
          />
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-2">
        <File className="w-5 h-5 text-gray-600" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
          <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
        </div>
      </div>
    );
  };

  const filteredConversations = conversations
    .filter(conv => {
      const matchesSearch = conv.employee_name?.toLowerCase().includes(searchQuery.toLowerCase());
      const isArchived = archivedChats.includes(conv.employee_id);
      
      if (activeTab === 'open') {
        return matchesSearch && !isArchived;
      } else {
        return matchesSearch && isArchived;
      }
    })
    .sort((a, b) => {
      // Pinned chats first
      const aPinned = pinnedChats.includes(a.employee_id);
      const bPinned = pinnedChats.includes(b.employee_id);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      // Then by last message time
      return new Date(b.last_message_time) - new Date(a.last_message_time);
    });
    
  const filteredMessages = messageSearchQuery
    ? messages.filter(msg => 
        msg.message?.toLowerCase().includes(messageSearchQuery.toLowerCase())
      )
    : messages;

  const filteredEmployees = employees.filter(emp => {
    const name = emp.User 
      ? `${emp.User.first_name || ''} ${emp.User.last_name || ''}`.trim()
      : `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="flex h-[calc(100vh-120px)] bg-gray-50">
      {/* Sidebar - Conversations List */}
      <div className={`${selectedConversation ? 'hidden lg:flex' : 'flex'} w-full lg:w-96 flex-col bg-white border-r border-gray-200`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (!('Notification' in window)) {
                    toast.error('Notifications not supported');
                    return;
                  }
                  if (Notification.permission === 'granted') {
                    toast.info('Notifications already enabled');
                  } else if (Notification.permission === 'denied') {
                    toast.error('Notifications blocked. Enable in browser settings.');
                  } else {
                    Notification.requestPermission().then(permission => {
                      if (permission === 'granted') {
                        notificationPermissionRef.current = true;
                        toast.success('Notifications enabled!');
                        // Test notification
                        new Notification('Notifications Enabled', {
                          body: 'You will now receive message notifications',
                          icon: '/logo.png'
                        });
                      } else {
                        toast.error('Notification permission denied');
                      }
                    });
                  }
                }}
                className={`p-2 rounded-full transition-colors shadow-sm ${
                  notificationPermissionRef.current 
                    ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                title={notificationPermissionRef.current ? 'Notifications enabled' : 'Enable notifications'}
              >
                <Bell className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowNewChatModal(true)}
                className="p-2 bg-[#DF6B38] text-white rounded-full hover:bg-[#c55a2a] transition-colors shadow-md hover:shadow-lg"
                title="New conversation"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 text-gray-900 placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DF6B38] border border-gray-200"
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setActiveTab('open')}
              className={`flex-1 py-2 px-4 rounded-md font-medium text-sm transition-all ${
                activeTab === 'open'
                  ? 'bg-white text-[#DF6B38] shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Open
            </button>
            <button
              onClick={() => setActiveTab('closed')}
              className={`flex-1 py-2 px-4 rounded-md font-medium text-sm transition-all ${
                activeTab === 'closed'
                  ? 'bg-white text-[#DF6B38] shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Archived
            </button>
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-4 text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No conversations</h3>
              <p className="text-sm text-gray-500 mb-4">Start messaging your colleagues</p>
              <button
                onClick={() => setShowNewChatModal(true)}
                className="px-4 py-2 bg-[#DF6B38] text-white rounded-lg hover:bg-[#c55a2a] transition-colors shadow-md"
              >
                New Message
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredConversations.map((conv) => (
                <div
                  key={conv.employee_id}
                  onClick={() => setSelectedConversation(conv)}
                  className={`p-4 cursor-pointer transition-all ${
                    selectedConversation?.employee_id === conv.employee_id 
                      ? 'bg-blue-50 border-l-4 border-[#DF6B38]' 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      {conv.cdn_avatar_url || conv.avatar_url ? (
                        <img
                          src={conv.cdn_avatar_url || conv.avatar_url}
                          alt={conv.employee_name}
                          className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-200"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#16314C] to-[#DF6B38] flex items-center justify-center text-white font-bold text-sm ring-2 ring-gray-200">
                          {getInitials(conv.employee_name)}
                        </div>
                      )}
                      <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"></div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          {pinnedChats.includes(conv.employee_id) && (
                            <Pin className="w-3.5 h-3.5 text-[#DF6B38] flex-shrink-0" />
                          )}
                          {/* Position as primary identifier */}
                          <div className="flex flex-col flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 truncate text-sm">
                              {conv.position || 'Employee'}
                            </h3>
                            <p className="text-xs text-gray-500 truncate">
                              {conv.employee_name || 'Unknown User'}
                            </p>
                          </div>
                          {mutedChats.includes(conv.employee_id) && (
                            <BellOff className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          )}
                        </div>
                        <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                          {formatTime(conv.last_message_time)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-gray-600 truncate flex-1">
                          {conv.last_message || 'No messages yet'}
                        </p>
                        {conv.unread_count > 0 && (
                          <span className="px-2 py-0.5 bg-[#DF6B38] text-white text-xs rounded-full font-medium flex-shrink-0">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      {selectedConversation ? (
        <div className="flex-1 flex flex-col bg-white">
          {/* Chat Header */}
          <div className="p-4 border-b border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-700" />
                </button>
                
                {selectedConversation.cdn_avatar_url || selectedConversation.avatar_url ? (
                  <div className="relative">
                    <img
                      src={selectedConversation.cdn_avatar_url || selectedConversation.avatar_url}
                      alt={selectedConversation.employee_name}
                      className="w-11 h-11 rounded-full object-cover ring-2 ring-gray-200"
                    />
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#16314C] to-[#DF6B38] flex items-center justify-center text-white font-bold text-sm ring-2 ring-gray-200">
                      {getInitials(selectedConversation.employee_name)}
                    </div>
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                )}
                
                <div>
                  <h2 className="font-semibold text-gray-900 flex items-center gap-2 text-base">
                    {selectedConversation.position || 'Employee'}
                  </h2>
                  <p className="text-xs text-gray-500 truncate">
                    {selectedConversation.employee_name || 'Unknown User'}
                  </p>
                  <div className="flex items-center gap-1.5 text-xs">
                    {recipientTyping ? (
                      <span className="text-[#DF6B38] font-medium">typing...</span>
                    ) : selectedConversation.last_message_time ? (
                      <>
                        <Circle className="w-2 h-2 text-gray-400 fill-current" />
                        <span className="text-gray-500">Last seen {formatTime(selectedConversation.last_message_time)}</span>
                      </>
                    ) : (
                      <>
                        <Circle className="w-2 h-2 text-gray-400 fill-current" />
                        <span className="text-gray-500">Offline</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button 
                  onClick={() => navigate('/collaboration/video-conference')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600" 
                  title="Voice call"
                >
                  <Phone className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => navigate('/collaboration/video-conference')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600" 
                  title="Video call"
                >
                  <Video className="w-5 h-5" />
                </button>
                <div className="relative">
                  <button 
                    onClick={() => setShowChatMenu(!showChatMenu)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600" 
                    title="More options"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>
                  
                  {showChatMenu && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setShowChatMenu(false)}
                      />
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-20">
                        <button
                          onClick={() => {
                            togglePinChat(selectedConversation.employee_id);
                            setShowChatMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Pin className="w-4 h-4" />
                          {pinnedChats.includes(selectedConversation.employee_id) ? 'Unpin chat' : 'Pin chat'}
                        </button>
                        <button
                          onClick={() => {
                            toggleMuteChat(selectedConversation.employee_id);
                            setShowChatMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        >
                          <BellOff className="w-4 h-4" />
                          {mutedChats.includes(selectedConversation.employee_id) ? 'Unmute' : 'Mute notifications'}
                        </button>
                        <button
                          onClick={() => {
                            setMessageSearchQuery('');
                            setShowChatMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Search className="w-4 h-4" />
                          Search in chat
                        </button>
                        <div className="border-t border-gray-200 my-1" />
                        <button
                          onClick={() => {
                            toggleArchiveChat(selectedConversation.employee_id);
                            setShowChatMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                        >
                          <Archive className="w-4 h-4" />
                          Archive chat
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div 
            className="flex-1 overflow-y-auto p-4 relative" 
            ref={messagesContainerRef}
            style={{
              backgroundColor: '#f0f2f5',
              backgroundImage: `
                radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.03) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(255, 177, 66, 0.03) 0%, transparent 50%),
                radial-gradient(circle at 40% 20%, rgba(66, 165, 245, 0.02) 0%, transparent 50%),
                repeating-linear-gradient(
                  0deg,
                  transparent,
                  transparent 2px,
                  rgba(255, 255, 255, 0.03) 2px,
                  rgba(255, 255, 255, 0.03) 4px
                )
              `
            }}
          >
            {/* Message Search Bar */}
            {messageSearchQuery !== '' && (
              <div className="mb-4 p-3 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search in this conversation..."
                    value={messageSearchQuery}
                    onChange={(e) => setMessageSearchQuery(e.target.value)}
                    autoFocus
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DF6B38] bg-gray-50"
                  />
                  <button
                    onClick={() => setMessageSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {filteredMessages.length} message{filteredMessages.length !== 1 ? 's' : ''} found
                </p>
              </div>
            )}
            
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#DF6B38]"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-md">
                  <Lock className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Start the conversation</h3>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Shield className="w-4 h-4 text-green-500" />
                  End-to-end encrypted
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredMessages.map((msg, index) => {
                  const isOwnMessage = msg.from_employee_id === currentEmployeeId;
                  const showAvatar = index === 0 || messages[index - 1].from_employee_id !== msg.from_employee_id;
                  const showName = showAvatar && !isOwnMessage; // Show name for other person's messages
                  
                  // Get sender avatar and name
                  const senderAvatar = isOwnMessage ? userAvatar : (msg.from_employee_avatar || selectedConversation.cdn_avatar_url || selectedConversation.avatar_url);
                  const senderName = isOwnMessage 
                    ? (`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'You')
                    : (msg.from_employee_name || selectedConversation.employee_name || 'Unknown User');
                  
                  return (
                    <div key={msg.id}>
                      {/* Show sender name above message bubble for received messages */}
                      {showName && (
                        <div className="flex items-center gap-2 mb-1 ml-10">
                          <p className="text-xs font-medium text-gray-600">{senderName}</p>
                        </div>
                      )}
                      
                      <div
                        className={`flex items-end gap-2 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}
                      >
                        {/* Avatar */}
                        <div className="w-8 h-8 flex-shrink-0">
                          {showAvatar && (
                            senderAvatar ? (
                              <img
                                src={senderAvatar}
                                alt={senderName}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                isOwnMessage ? 'bg-[#DF6B38]' : 'bg-[#16314C]'
                              }`}>
                                {getInitials(senderName)}
                              </div>
                            )
                          )}
                        </div>

                        {/* Message Bubble */}
                        <div className={`max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'} group`}>
                          <div
                            className={`rounded-2xl px-4 py-2.5 relative shadow-sm ${
                              msg.is_deleted
                                ? 'bg-gray-100 border border-gray-300'
                                : isOwnMessage
                                ? 'bg-[#DF6B38] text-white'
                                : 'bg-white text-gray-900 border border-gray-200'
                            }`}
                          >
                            {msg.is_deleted ? (
                              <p className="text-sm text-gray-500 italic">
                                🚫 This message was deleted
                              </p>
                            ) : (
                              <>
                                {/* File attachment preview (if exists) */}
                                {msg.file_url && (
                                  <div className="mb-2">
                                    {/* Voice Message */}
                                    {(msg.file_type === 'audio' || msg.file_url.match(/\.(webm|mp3|wav|ogg|m4a)$/i) || msg.message?.includes('🎤')) ? (
                                      <div className={`flex items-center gap-3 p-3 rounded-lg ${
                                        isOwnMessage ? 'bg-white bg-opacity-20' : 'bg-white'
                                      }`}>
                                        <button
                                          onClick={() => toggleAudioPlayback(msg.file_url, msg.id)}
                                          className={`p-2 rounded-full transition-colors ${
                                            isOwnMessage 
                                              ? 'bg-white bg-opacity-30 hover:bg-opacity-40' 
                                              : 'bg-[#DF6B38] hover:bg-[#c55a2a]'
                                          }`}
                                        >
                                          {playingAudio === msg.id ? (
                                            <Pause className={`w-5 h-5 ${isOwnMessage ? 'text-white' : 'text-white'}`} />
                                          ) : (
                                            <Play className={`w-5 h-5 ${isOwnMessage ? 'text-white' : 'text-white'}`} />
                                          )}
                                        </button>
                                        <div className="flex-1 flex items-center gap-2">
                                          {/* Waveform visualization */}
                                          <div className="flex-1 flex items-center gap-0.5 h-8">
                                            {[...Array(20)].map((_, i) => (
                                              <div
                                                key={i}
                                                className={`flex-1 rounded-full transition-all ${
                                                  isOwnMessage ? 'bg-white bg-opacity-50' : 'bg-gray-300'
                                                }`}
                                                style={{
                                                  height: `${20 + Math.sin(i * 0.5) * 15}px`,
                                                  opacity: playingAudio === msg.id && i < 10 ? 1 : 0.5
                                                }}
                                              />
                                            ))}
                                          </div>
                                          <span className={`text-xs font-medium ${isOwnMessage ? 'text-white' : 'text-gray-600'}`}>
                                            {msg.message?.match(/\(([^)]+)\)/)?.[1] || '0:00'}
                                          </span>
                                        </div>
                                      </div>
                                    ) : msg.file_type === 'image' || msg.file_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                      // Image preview
                                      <div className="relative group/image">
                                        <img 
                                          src={msg.file_url} 
                                          alt="Shared image"
                                          className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                          style={{ maxHeight: '200px', maxWidth: '250px', objectFit: 'cover' }}
                                          onClick={() => setViewingImage(msg.file_url)}
                                        />
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(msg.file_url, '_blank');
                                          }}
                                          className="absolute top-2 right-2 p-2 bg-black bg-opacity-50 text-white rounded-full opacity-0 group-hover/image:opacity-100 transition-opacity hover:bg-opacity-70"
                                          title="Download image"
                                        >
                                          <Download className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ) : (
                                      // File download card
                                      <button
                                        onClick={() => window.open(msg.file_url, '_blank')}
                                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                                          isOwnMessage 
                                            ? 'bg-white bg-opacity-20 hover:bg-opacity-30' 
                                            : 'bg-gray-100 hover:bg-gray-200'
                                        }`}
                                      >
                                        <div className={`p-2 rounded-lg ${isOwnMessage ? 'bg-white bg-opacity-20' : 'bg-[#DF6B38]'}`}>
                                          <File className={`w-5 h-5 ${isOwnMessage ? 'text-white' : 'text-white'}`} />
                                        </div>
                                        <div className="flex-1 min-w-0 text-left">
                                          <p className={`text-sm font-medium truncate ${isOwnMessage ? 'text-white' : 'text-gray-900'}`}>
                                            {msg.file_url.split('/').pop() || 'File attachment'}
                                          </p>
                                          <p className={`text-xs ${isOwnMessage ? 'text-white text-opacity-70' : 'text-gray-500'}`}>
                                            Click to download
                                          </p>
                                        </div>
                                        <Download className={`w-5 h-5 ${isOwnMessage ? 'text-white' : 'text-gray-600'}`} />
                                      </button>
                                    )}
                                  </div>
                                )}
                                
                                {/* Message text */}
                                {msg.message && !msg.message.startsWith('📎') && !msg.message.startsWith('🎤') && (
                                  <p className="text-sm break-words whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                                )}
                              </>
                            )}
                            
                            {/* Message actions on hover */}
                            {!msg.is_deleted && (
                              <div className={`absolute -top-8 ${isOwnMessage ? 'right-0' : 'left-0'} opacity-0 group-hover:opacity-100 transition-opacity bg-white shadow-lg rounded-lg border border-gray-200 flex items-center z-10`}>
                                {/* Reactions */}
                                <div className="relative">
                                  <button
                                    onClick={() => setShowReactionPicker(showReactionPicker === msg.id ? null : msg.id)}
                                    className="p-2 hover:bg-gray-50 text-gray-600 rounded-lg transition-colors"
                                    title="React"
                                  >
                                    <Smile className="w-4 h-4" />
                                  </button>
                                  
                                  {showReactionPicker === msg.id && (
                                    <>
                                      <div 
                                        className="fixed inset-0 z-20" 
                                        onClick={() => setShowReactionPicker(null)}
                                      />
                                      <div className="absolute bottom-full mb-2 left-0 bg-white shadow-xl rounded-lg border border-gray-200 p-2 flex gap-1 z-30">
                                        {['👍', '❤️', '😂', '😮', '😢', '🙏'].map(emoji => (
                                          <button
                                            key={emoji}
                                            onClick={() => addReaction(msg.id, emoji)}
                                            className="text-xl hover:scale-125 transition-transform p-1"
                                          >
                                            {emoji}
                                          </button>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                                
                                {/* Reply */}
                                <button
                                  onClick={() => setReplyingTo(msg)}
                                  className="p-2 hover:bg-gray-50 text-gray-600 rounded-lg transition-colors"
                                  title="Reply"
                                >
                                  <Reply className="w-4 h-4" />
                                </button>
                                
                                {/* Forward */}
                                <button
                                  onClick={() => setShowForwardModal(msg)}
                                  className="p-2 hover:bg-gray-50 text-gray-600 rounded-lg transition-colors"
                                  title="Forward"
                                >
                                  <Forward className="w-4 h-4" />
                                </button>
                                
                                {/* Star */}
                                <button
                                  onClick={() => toggleStarMessage(msg.id)}
                                  className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
                                  title={starredMessages.includes(msg.id) ? "Unstar" : "Star"}
                                >
                                  <Star className={`w-4 h-4 ${starredMessages.includes(msg.id) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}`} />
                                </button>
                                
                                {/* Edit (own messages only, within 15 min) */}
                                {isOwnMessage && (() => {
                                  const messageAge = Date.now() - new Date(msg.created_at).getTime();
                                  const fifteenMinutes = 15 * 60 * 1000;
                                  return messageAge < fifteenMinutes && (
                                    <button
                                      onClick={() => startEditMessage(msg)}
                                      className="p-2 hover:bg-gray-50 text-gray-600 rounded-lg transition-colors"
                                      title="Edit message (within 15 minutes)"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                  );
                                })()}
                                
                                {/* Delete (own messages only, within 2 min) */}
                                {isOwnMessage && (() => {
                                  const messageAge = Date.now() - new Date(msg.created_at).getTime();
                                  const twoMinutes = 2 * 60 * 1000;
                                  return messageAge < twoMinutes && (
                                    <button
                                      onClick={() => handleDeleteMessage(msg.id)}
                                      className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                                      title="Delete message (within 2 minutes)"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  );
                                })()}
                              </div>
                            )}
                            
                            {/* Reactions display below message */}
                            {messageReactions[msg.id] && messageReactions[msg.id].length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {Object.entries(
                                  messageReactions[msg.id].reduce((acc, r) => {
                                    acc[r.emoji] = (acc[r.emoji] || 0) + 1;
                                    return acc;
                                  }, {})
                                ).map(([emoji, count]) => (
                                  <span 
                                    key={emoji}
                                    className="px-2 py-0.5 bg-gray-100 rounded-full text-xs flex items-center gap-1"
                                  >
                                    {emoji} {count}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className={`flex items-center gap-1.5 mt-1 px-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                            {starredMessages.includes(msg.id) && (
                              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                            )}
                            {msg.is_edited && (
                              <span className="text-xs text-gray-400 italic">edited</span>
                            )}
                            <span className="text-xs text-gray-500">{formatTime(msg.created_at)}</span>
                            {isOwnMessage && (
                              msg.is_read ? (
                                <CheckCheck className="w-3.5 h-3.5 text-blue-500" />
                              ) : (
                                <Check className="w-3.5 h-3.5 text-gray-400" />
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Message Input */}
          <div className="p-4 border-t border-gray-200 bg-white">
            {/* Reply indicator */}
            {replyingTo && (
              <div className="mb-3 p-3 bg-blue-50 rounded-lg border-l-4 border-[#DF6B38] relative">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Reply className="w-4 h-4 text-[#DF6B38]" />
                      <p className="text-xs font-semibold text-gray-700">
                        Replying to {replyingTo.from_employee_id === currentEmployeeId ? 'yourself' : replyingTo.from_employee_name}
                      </p>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{replyingTo.message}</p>
                  </div>
                  <button
                    onClick={() => setReplyingTo(null)}
                    className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                    title="Cancel reply"
                  >
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
            )}
            
            {/* Edit indicator */}
            {editingMessage && (
              <div className="mb-3 p-3 bg-amber-50 rounded-lg border-l-4 border-amber-500 relative">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Edit2 className="w-4 h-4 text-amber-600" />
                      <p className="text-xs font-semibold text-gray-700">Editing message</p>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{editingMessage.message}</p>
                  </div>
                  <button
                    onClick={cancelEdit}
                    className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                    title="Cancel edit"
                  >
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
            )}
            
            {selectedFile && (
              <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200 relative">
                {getFilePreview(selectedFile)}
                <button
                  onClick={() => setSelectedFile(null)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  title="Remove file"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            
            {/* Voice Recording Preview */}
            {audioBlob && audioPreviewUrl && (
              <div className="mb-3 p-4 bg-blue-50 rounded-lg border-l-4 border-[#DF6B38]">
                <div className="flex items-center gap-3">
                  {/* Hidden audio element for preview */}
                  <audio 
                    src={audioPreviewUrl} 
                    className="hidden" 
                    id="voice-preview-audio"
                  />
                  
                  <button
                    onClick={() => {
                      try {
                        const audioElement = document.getElementById('voice-preview-audio');
                        if (audioElement) {
                          if (audioElement.paused) {
                            audioElement.play();
                            toast.success('Playing preview');
                          } else {
                            audioElement.pause();
                            audioElement.currentTime = 0;
                          }
                        }
                      } catch (error) {
                        console.error('Preview error:', error);
                        toast.error('Failed to play preview');
                      }
                    }}
                    className="p-2 bg-[#DF6B38] text-white rounded-full hover:bg-[#c55a2a] transition-colors"
                  >
                    <Play className="w-5 h-5" />
                  </button>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-700">Voice message recorded</p>
                    <p className="text-xs text-gray-500">Duration: {formatDuration(recordingTime)} • {(audioBlob.size / 1024).toFixed(0)}KB</p>
                  </div>
                  <button
                    onClick={sendVoiceMessage}
                    className="px-4 py-2 bg-[#DF6B38] text-white rounded-lg hover:bg-[#c55a2a] transition-colors font-medium"
                  >
                    Send
                  </button>
                  <button
                    onClick={cancelRecording}
                    className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                    title="Cancel"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
            )}
            
            {/* Recording Indicator */}
            {isRecording && (
              <div className="mb-3 p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    <Mic className="w-5 h-5 text-red-500 animate-pulse" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-700">Recording...</p>
                    <p className="text-xs text-gray-500">{formatDuration(recordingTime)}</p>
                  </div>
                  <button
                    onClick={stopRecording}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                  >
                    Stop
                  </button>
                  <button
                    onClick={cancelRecording}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              />
              
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                title="Attach file"
                disabled={isRecording}
              >
                <Paperclip className="w-5 h-5" />
              </button>

              {/* Mic button - hold to record */}
              {!messageInput.trim() && !selectedFile && !audioBlob && !isRecording && (
                <button
                  onMouseDown={startRecording}
                  onMouseUp={stopRecording}
                  onMouseLeave={stopRecording}
                  onTouchStart={startRecording}
                  onTouchEnd={stopRecording}
                  className="p-2.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                  title="Hold to record voice message"
                >
                  <Mic className="w-5 h-5" />
                </button>
              )}

              <input
                type="text"
                value={messageInput}
                onChange={handleTyping}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder={isRecording ? "Recording..." : "Type a message..."}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#DF6B38] focus:border-transparent bg-gray-50"
                disabled={isRecording || audioBlob}
              />

              <button
                onClick={handleSendMessage}
                disabled={!messageInput.trim() && !selectedFile && !audioBlob}
                className="p-3 bg-[#DF6B38] text-white rounded-full hover:bg-[#c55a2a] hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                title={editingMessage ? 'Save edit' : 'Send message'}
              >
                {editingMessage ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 hidden lg:flex flex-col items-center justify-center bg-gray-50 text-center p-8">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 shadow-md">
            <Users className="w-10 h-10 text-[#DF6B38]" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Select a conversation</h2>
          <p className="text-gray-600 max-w-md">
            Choose from your existing conversations or start a new one
          </p>
        </div>
      )}

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">New Message</h2>
                <button
                  onClick={() => {
                    setShowNewChatModal(false);
                    setSearchQuery('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search colleagues..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DF6B38] focus:border-transparent bg-gray-50"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {filteredEmployees.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">No colleagues found</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredEmployees.map((emp) => {
                    const name = emp.User 
                      ? `${emp.User.first_name || ''} ${emp.User.last_name || ''}`.trim()
                      : `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                    const avatar = emp.cdn_avatar_url || emp.avatar || emp.User?.cdn_avatar_url || emp.User?.avatar;
                    
                    return (
                      <button
                        key={emp.id}
                        onClick={() => startNewChat(emp)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors text-left"
                      >
                        {avatar ? (
                          <img
                            src={avatar}
                            alt={name}
                            className="w-11 h-11 rounded-full object-cover ring-2 ring-gray-200"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#16314C] to-[#DF6B38] flex items-center justify-center text-white font-bold text-sm ring-2 ring-gray-200">
                            {getInitials(name)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{emp.position || 'Employee'}</h3>
                          <p className="text-sm text-gray-500 truncate">{name || 'Unknown User'}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Forward Message Modal */}
      {showForwardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Forward className="w-5 h-5" />
                  Forward Message
                </h2>
                <button
                  onClick={() => setShowForwardModal(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700 line-clamp-2">{showForwardModal.message}</p>
              </div>
              
              <div className="relative mt-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search recipients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DF6B38] bg-gray-50"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {filteredEmployees.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">No recipients found</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredEmployees.map((emp) => {
                    const name = emp.User 
                      ? `${emp.User.first_name || ''} ${emp.User.last_name || ''}`.trim()
                      : `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                    const avatar = emp.cdn_avatar_url || emp.avatar || emp.User?.cdn_avatar_url || emp.User?.avatar;
                    
                    return (
                      <button
                        key={emp.id}
                        onClick={() => forwardMessage(showForwardModal, emp.id)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors text-left"
                      >
                        {avatar ? (
                          <img
                            src={avatar}
                            alt={name}
                            className="w-11 h-11 rounded-full object-cover ring-2 ring-gray-200"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#16314C] to-[#DF6B38] flex items-center justify-center text-white font-bold text-sm ring-2 ring-gray-200">
                            {getInitials(name)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{emp.position || 'Employee'}</h3>
                          <p className="text-sm text-gray-500 truncate">{name || 'Unknown User'}</p>
                        </div>
                        <Forward className="w-5 h-5 text-gray-400" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {viewingImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setViewingImage(null)}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <button
              onClick={() => setViewingImage(null)}
              className="absolute top-4 right-4 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-colors z-10"
              title="Close"
            >
              <X className="w-6 h-6" />
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(viewingImage, '_blank');
              }}
              className="absolute top-4 right-16 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-colors z-10"
              title="Download"
            >
              <Download className="w-6 h-6" />
            </button>

            <img 
              src={viewingImage} 
              alt="Full size"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernMessaging;
